import { HttpErrorResponse } from "@angular/common/http";
import { inject, WritableSignal, signal, computed, Injectable, Signal, ResourceRef } from "@angular/core";
import { rxResource, toSignal } from "@angular/core/rxjs-interop";
import { map, debounceTime, of, delay } from "rxjs";
import { PropertyInformation, PropertyInformationDto } from "src/models/estate/property-information.model";
import { EstateDataService } from "../estate-data.service";
import { SalesDataService } from "../sales-data.service";
import { ImagePackage } from "src/models/common/image.model";
import { ResourceService } from "../resources/resource.service";
import { UnitDataService } from "../unit-data.service";
import { EVServiceType } from "src/util/auth0/models/evServiceType.enum";
import { EvAuthorizationService } from "src/util/auth0/ev-auth.service";

@Injectable({
    providedIn: "root",
})
export class StockCoperativeDataService {
    private readonly authService = inject(EvAuthorizationService);
    private estateDataService = inject(EstateDataService);
    private salesDataService = inject(SalesDataService);
    private resourceService = inject(ResourceService);
    private readonly unitDataService = inject(UnitDataService);

    private unitId: WritableSignal<number | null> = signal(null);
    private fetchPropertyInformation: WritableSignal<boolean> = signal(false);
    private fetchPublicAddressInfo: WritableSignal<boolean> = signal(false);
    private hasPhysicalExposuresRole = toSignal(this.authService.hasPermission(EVServiceType.EvWeb_PhysicalExposure));

    private buildingsTrigger = computed(() => {
        return this.fetchPropertyInformation() ? this.unitId() : null;
    });
    private publicAddressInfoTrigger = computed(() => {
        return this.fetchPublicAddressInfo() ? this.unitId() : null;
    });

    stockCooperativeInfo = computed(() => {
        if (this.stockCooperativeInfoResource.hasValue()) {
            return this.stockCooperativeInfoResource.value();
        }
        return null;
    });

    imagePackages: Signal<ImagePackage[]> = computed(() => {
        let imagePackages: ImagePackage[] = [];
        if (this.blomImagesResource.hasValue()) {
            imagePackages = [...imagePackages, ...this.blomImagesResource.value()];
        }
        return imagePackages;
    });

    position = computed(() => {
        const keyInfo = this.stockCooperativeInfo();
        const hasValidValues = keyInfo?.mapLatitude != null && keyInfo?.mapLongitude != null && keyInfo?.mapLatitude !== 0 && keyInfo?.mapLongitude !== 0;
        if (hasValidValues) {
            return {
                lat: keyInfo.mapLatitude,
                lng: keyInfo.mapLongitude,
            };
        }
        return null;
    });

    stockCooperativeInfoIsLoading = computed(() => this.stockCooperativeInfoResource.isLoading());
    stockCooperativeInfoError = computed(() => this.stockCooperativeInfoResource.error() as HttpErrorResponse);

    setUnitId(unitId: number): void {
        this.unitId.set(unitId);
    }

    triggerLoadPropertyInformation(): void {
        this.fetchPropertyInformation.set(true);
    }

    triggerLoadPublicAddressInfo(): void {
        this.fetchPublicAddressInfo.set(true);
    }

    propertyInformationResource = this.estateDataService.getPropertyInformationResource(this.buildingsTrigger);
    publicAddressInfoResource = this.unitDataService.getPublicAddressInfoResource(this.publicAddressInfoTrigger);
    stockCooperativeInfoResource = this.unitDataService.getOrganizationUnitKeyInfoResource(this.unitId);
    unitShareSalesResource = this.salesDataService.getShareSalesInformationResource(this.unitId);
    unitShareInfoResource = this.unitDataService.getUnitShareInfoResource(this.unitId);

    private readonly blomImagesResource: ResourceRef<ImagePackage[]> = rxResource({
        params: () => this.position(),
        stream: ({ params: position }) => {
            if (position) {
                return this.resourceService.getBlomImagePackage({ lat: position.lat, lng: position.lng });
            }
            return of([]);
        },
    });

    physicalRiskResource = this.estateDataService.getPhysicalRiskResource(this.unitId, this.hasPhysicalExposuresRole);

    physicalExposures = computed(() => {
        if (this.physicalRiskResource.hasValue()) {
            return this.physicalRiskResource.value();
        }
        return null;
    });
}
